import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { twMerge as twm } from 'tailwind-merge'

import { Adjustment } from '../../models/Adjustment'
import { BaseRecord } from '../../models/BaseRecord'
import { Line } from '../../models/Line'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { ComponentData } from '../../utils/typing'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { Chip } from '../common/fields/Chip'
import { CurrencyField } from '../common/fields/CurrencyField'
import { TextField } from '../common/fields/TextField'
import { WorkflowPage } from '../workflows/WorkflowPage'

const viewWillAppear = async ({
    urlParams,
    props: { action },
}: PocketComponent<{ action: 'new' | 'edit' }>) => {
    const defaultAdjustment = await (async (): Promise<Adjustment | null> => {
        switch (action) {
            case 'new': {
                if (urlParams.itemID === undefined) {
                    return null
                }
                const item = await RecordFactory.getInstance().loadItem(urlParams.itemID)
                if (item === undefined) {
                    return null
                }
                const adjustment = Adjustment.initWithItem(item)
                adjustment.type = Adjustment.adjustmentStrings()[0].value
                const line = Line.initWithItem(item, adjustment)
                line.total = undefined
                adjustment.lines = [line]
                return adjustment
            }
            case 'edit': {
                if (urlParams.adjustmentID === undefined) {
                    return null
                }
                const adjustment = await RecordFactory.getInstance().loadAdjustment(
                    urlParams.adjustmentID
                )
                if (adjustment === undefined) {
                    return null
                }
                if (adjustment.lines.length > 0) {
                    adjustment.lines = [adjustment.lines[0].clone()]
                }
                return adjustment
            }
        }
    })()
    if (defaultAdjustment === null) {
        return new RecordNotFoundError()
    }

    return { defaultAdjustment }
}

const render = ({
    data,
    base: { logAndDisplayError },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const navigate = useNavigate()

    const [adjustment, setAdjustment] = useState(data.defaultAdjustment)
    const [isLoading, setIsLoading] = useState(false)

    const [isLearnMoreModalOpen, setIsLearnMoreModalOpen] = useState(false)
    const [isUpdateConfirmationModalOpen, setIsUpdateConfirmationModalOpen] = useState(false)
    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false)

    const hasEdits = BaseRecord.hasChanges(adjustment, data.defaultAdjustment)

    const handleSave = async (shouldSkipConfirmation = false) => {
        if (adjustment.id !== undefined && !shouldSkipConfirmation) {
            setIsUpdateConfirmationModalOpen(true)
            return
        }

        const newAdjustment = adjustment.clone()
        newAdjustment.lines[0].quantity =
            Math.abs(adjustment.quantity ?? 0) * adjustment.adjustmentSign

        setIsLoading(true)
        const result = await RecordFactory.getInstance().sendAdjustment(newAdjustment)
        setIsLoading(false)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        navigate('/dashboards/inventory')
    }

    const handleDelete = async () => {
        setIsLoading(true)
        await RecordFactory.getInstance().deleteAdjustment(adjustment)
        setIsLoading(false)
        navigate('/dashboards/inventory')
    }

    return (
        <WorkflowPage
            title="Change quantity"
            onClose={() => navigate(-1)}
            footerContent={
                <div
                    className={twm(
                        'flex items-center',
                        adjustment.id !== undefined ? 'justify-between' : 'justify-end'
                    )}
                >
                    {adjustment.id !== undefined && (
                        <Button
                            intent="destructive"
                            onClick={() => setIsDeleteConfirmationModalOpen(true)}
                        >
                            Delete
                        </Button>
                    )}

                    <Button
                        intent="primary"
                        disabled={!hasEdits}
                        isLoading={isLoading}
                        onClick={() => handleSave()}
                    >
                        Save
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card>
                    <WorkflowPage.IconSection title="Product" icon="package.large">
                        <TextField value={adjustment.item?.name ?? ''} disabled size="medium" />
                    </WorkflowPage.IconSection>

                    {adjustment.id !== undefined && (
                        <WorkflowPage.IconSection
                            title="Date"
                            icon="calendar.large"
                            description={adjustment.createdDate?.format('MMM D, YYYY')}
                        />
                    )}

                    <WorkflowPage.IconSection title="Type (required)" icon="settings.large">
                        <div className="flex flex-wrap items-center justify-start gap-x-2 gap-y-2">
                            {Adjustment.adjustmentStrings().map((option) => (
                                <Chip
                                    key={option.value}
                                    title={option.label}
                                    isSelected={adjustment.type === option.value}
                                    onClick={() =>
                                        setAdjustment(adjustment.clone({ type: option.value }))
                                    }
                                    size="small"
                                />
                            ))}
                        </div>
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Quantity (required)" icon="hash.large">
                        <TextField
                            type="number"
                            size="medium"
                            min={0}
                            isFullWidth
                            value={Math.abs(adjustment.quantity ?? 0)}
                            onChange={(event) =>
                                setAdjustment(
                                    adjustment.clone({
                                        lines: [
                                            adjustment.lines[0].clone({
                                                quantity: parseInt(event.target.value),
                                            }),
                                        ],
                                    })
                                )
                            }
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Total cost (required)"
                        icon="coins.large"
                        thirdLine={
                            <Button
                                onClick={() => setIsLearnMoreModalOpen(true)}
                                intent="tertiaryLink"
                                className="px-0"
                            >
                                Learn more
                            </Button>
                        }
                    >
                        <CurrencyField
                            value={adjustment.total ?? 0}
                            onChange={(value) => setAdjustment(adjustment.clone({ total: value }))}
                            size="medium"
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>
            </div>

            <Modal
                isOpen={isLearnMoreModalOpen}
                onClose={() => setIsLearnMoreModalOpen(false)}
                title="About total cost"
                showDividers={false}
                size="small"
                footer={
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button intent="primary" onClick={() => setIsLearnMoreModalOpen(false)}>
                            Done
                        </Button>
                    </div>
                }
            >
                Total cost is the total amount of money associated with buying or selling the
                specified quantity of this product, whether you add to or reduce the inventory.
                Total cost = Quantity x Unit cost
            </Modal>

            <Modal
                isOpen={isUpdateConfirmationModalOpen}
                onClose={() => setIsUpdateConfirmationModalOpen(false)}
                title="Confirm change"
                showDividers={false}
                size="small"
                footer={
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button
                            intent="primary"
                            onClick={() => {
                                setIsUpdateConfirmationModalOpen(false)
                                handleSave(true)
                            }}
                        >
                            Update
                        </Button>
                    </div>
                }
            >
                Confirm that you'd like to update this record.
            </Modal>

            <Modal
                isOpen={isDeleteConfirmationModalOpen}
                onClose={() => setIsDeleteConfirmationModalOpen(false)}
                title="Confirm change"
                showDividers={false}
                size="small"
                footer={
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button
                            intent="primary"
                            onClick={() => {
                                setIsDeleteConfirmationModalOpen(false)
                                handleDelete()
                            }}
                        >
                            Delete
                        </Button>
                    </div>
                }
            >
                Confirm that you'd like to delete this record.
            </Modal>

            <NavigationBlockerPrompt when={hasEdits && !isLoading} />
        </WorkflowPage>
    )
}

export const AdjustmentPage = usePSComponent(render, viewWillAppear)
